import { useState, useEffect, useMemo } from 'react';
import {
  getExchangeRate,
  getExchangeRatesRange,
  getAllExchangeRates,
  getRateForDate,
  DEFAULT_EXCHANGE_RATE,
} from '../api/exchange_rates';

interface UseExchangeRatesOptions {
  // For single date mode
  date?: string;
  // For range mode
  dateRange?: [Date, Date];
  // Currency to convert (default: RMB)
  currency?: string;
  // Whether to fetch (can disable to prevent unnecessary fetches)
  enabled?: boolean;
  // Fetch ALL rates from DB (for comparing multiple seasons)
  fetchAll?: boolean;
}

interface UseExchangeRatesResult {
  // The rate to use for conversions (single date or average for range)
  rate: number;
  // For range: all rates by date
  ratesByDate: Map<string, number>;
  // Get rate for a specific date (with fallback logic)
  getRateForDate: (date: string) => number;
  // Stats for range
  average: number;
  min: number;
  max: number;
  // Whether using default rate (no DB data)
  isDefault: boolean;
  // Loading state
  loading: boolean;
  // Error if any
  error: string | null;
  // Info string for display (e.g., "7.20 (promedio)")
  rateInfo: string;
}

export function useExchangeRates(
  options: UseExchangeRatesOptions
): UseExchangeRatesResult {
  const { date, dateRange, currency = 'RMB', enabled = true, fetchAll = false } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ratesByDate, setRatesByDate] = useState<Map<string, number>>(
    new Map()
  );
  const [stats, setStats] = useState({
    average: DEFAULT_EXCHANGE_RATE,
    min: DEFAULT_EXCHANGE_RATE,
    max: DEFAULT_EXCHANGE_RATE,
    count: 0,
    isDefault: true,
  });

  // Fetch rates when date/range changes or fetchAll changes
  useEffect(() => {
    if (!enabled) {
      console.log('[useExchangeRates] Not enabled, skipping fetch');
      return;
    }

    const fetchRates = async () => {
      setLoading(true);
      setError(null);

      try {
        if (fetchAll) {
          // Fetch ALL rates for season comparison
          console.log('[useExchangeRates] Fetching ALL rates for season comparison');
          const result = await getAllExchangeRates(currency);
          setRatesByDate(result.rates);

          // Calculate stats from all rates
          if (result.count > 0) {
            const values = Array.from(result.rates.values());
            const sum = values.reduce((a, b) => a + b, 0);
            setStats({
              average: sum / values.length,
              min: Math.min(...values),
              max: Math.max(...values),
              count: result.count,
              isDefault: result.isDefault,
            });
          } else {
            setStats({
              average: DEFAULT_EXCHANGE_RATE,
              min: DEFAULT_EXCHANGE_RATE,
              max: DEFAULT_EXCHANGE_RATE,
              count: 0,
              isDefault: true,
            });
          }
        } else if (date) {
          // Single date mode
          console.log('[useExchangeRates] Fetching single date:', date);
          const result = await getExchangeRate(date, currency);
          console.log('[useExchangeRates] Result:', result);
          const rates = new Map<string, number>();
          rates.set(result.date, result.rate);
          setRatesByDate(rates);
          setStats({
            average: result.rate,
            min: result.rate,
            max: result.rate,
            count: 1,
            isDefault: result.isDefault,
          });
        } else if (dateRange) {
          // Range mode
          const from = dateRange[0].toISOString().split('T')[0];
          const to = dateRange[1].toISOString().split('T')[0];
          console.log('[useExchangeRates] Fetching range:', from, to);
          const result = await getExchangeRatesRange(from, to, currency);
          console.log('[useExchangeRates] Result:', result);
          setRatesByDate(result.rates);
          setStats({
            average: result.average,
            min: result.min,
            max: result.max,
            count: result.count,
            isDefault: result.isDefault,
          });
        } else {
          console.log('[useExchangeRates] No date or dateRange provided');
        }
      } catch (err) {
        console.error('[useExchangeRates] Error:', err);
        setError(err instanceof Error ? err.message : 'Error fetching rates');
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, [date, dateRange?.[0]?.getTime(), dateRange?.[1]?.getTime(), currency, enabled, fetchAll]);

  // Main rate to use
  const rate = useMemo(() => {
    if (date && ratesByDate.size > 0) {
      return getRateForDate(date, ratesByDate, DEFAULT_EXCHANGE_RATE);
    }
    return stats.average;
  }, [date, ratesByDate, stats.average]);

  // Function to get rate for a specific date
  const getRateForDateFn = useMemo(() => {
    return (targetDate: string) =>
      getRateForDate(targetDate, ratesByDate, stats.average);
  }, [ratesByDate, stats.average]);

  // Info string for display - just the rate value, tooltip explains details
  const rateInfo = useMemo(() => {
    if (stats.isDefault) {
      return `${DEFAULT_EXCHANGE_RATE.toFixed(2)} (por defecto)`;
    }
    if (dateRange && stats.count > 1) {
      return `${stats.average.toFixed(2)} (promedio)`;
    }
    return `${rate.toFixed(2)}`;
  }, [rate, stats, dateRange]);

  return {
    rate,
    ratesByDate,
    getRateForDate: getRateForDateFn,
    average: stats.average,
    min: stats.min,
    max: stats.max,
    isDefault: stats.isDefault,
    loading,
    error,
    rateInfo,
  };
}

export default useExchangeRates;
